import { useEffect, useState, useContext } from "react"
import { useSkeem } from "../providers/SkeemProvider"
import { RouterSessionContext } from "../Router"
import { IRecord } from "../types/schema"
import { errorIs } from "@cd2/error-box"
import { userTeamAttribute } from "../constants/userTeamSkeemAttribute"
import { Cache } from "../utils/cache"

type ILinkRecord = IRecord<
  "companyLinks",
  "name" | "link" | "createdAt" | "updatedAt" | "linkType" | "fileboyId" | "folder"
>

export type IUser = IRecord<
  "skeem_users",
  | "firstName"
  | "lastName"
  | "skeem_email"
  | "isManager"
  | "isOwner"
  | "isAdmin"
  | "isSuperAdmin"
  | "teams"
  | "termsAndConditions"
  | "payrollNumber"
  | "contactEmail"
  | "downloadPolicy"
  | "downloadPolicyContracts"
  | "requirePasswordChange"
  | "isHrProfessional"
  | "canCreateHRProfile"
  | "employee"
  | "isLeaver"
  | "teamsNotifiedFrom"
  | "isPayrollUser"
  | "useNewLoginLogic"
> & {
  managedTeams: IRecord<"teams", "name">[]
  team: IRecord<"teams", "name" | "downloadPolicy"> | null
  company: IRecord<
    "companies",
    | "addressCity"
    | "addressCounty"
    | "addressLine1"
    | "addressLine2"
    | "addressLine3"
    | "addressPostcode"
    | "canHaveTeams"
    | "chargeFrequency"
    | "charityNumber"
    | "email"
    | "imageFileboyId"
    | "letterHeadImageFileboyId"
    | "name"
    | "phoneNumber"
    | "planName"
    | "pricePennies"
    | "primaryColor"
    | "secondaryColor"
    | "userLimit"
    | "users"
    | "itrentUniqueIdentifier"
    | "itrentAlertEmails"
  > & {
    links: ILinkRecord[]
    plan: IRecord<
      "companyPlans",
      "name" | "pricePenniesPerUser" | "minUsers" | "maxUsers" | "includesOneHourHR"
    > | null
  }
}

const meCache = new Cache<Promise<IUser>>({
  timeoutSeconds: 1,
})
/** returns the current user; on failure, it will cause Router.tsx to call skeem.me() which will then trigger the routes rendered if auth has changed
 */
export function useFetchMe(deps: any[] = []) {
  const skeem = useSkeem()

  const { fetchMeAndSetRoles } = useContext(RouterSessionContext)
  const [user, setUser] = useState<null | IUser>(null)

  useEffect(() => {
    const fn = async () => {
      try {
        let mePromise = meCache.get("_")

        if (!mePromise) {
          mePromise = skeem.fetchMe<IUser>([
            "firstName",
            "lastName",
            "isManager",
            "isOwner",
            "isAdmin",
            "isSuperAdmin",
            "skeem_email",
            "teams",
            "termsAndConditions",
            "payrollNumber",
            "contactEmail",
            "downloadPolicy",
            "downloadPolicyContracts",
            "mfaEmail",
            "contactEmail",
            "requirePasswordChange",
            "isHrProfessional",
            "canCreateHRProfile",
            "isLeaver",
            "teamsNotifiedFrom",
            "isPayrollUser",
            "useNewLoginLogic",
            userTeamAttribute,
            { name: "employee" },
            {
              name: "managedTeams",
              attributes: ["name"],
            },
            {
              name: "company",
              attributes: [
                "addressCity",
                "addressCounty",
                "addressLine1",
                "addressLine2",
                "addressLine3",
                "addressPostcode",
                "canHaveTeams",
                "chargeFrequency",
                "charityNumber",
                "email",
                "imageFileboyId",
                "letterHeadImageFileboyId",
                "name",
                "phoneNumber",
                "planName",
                "pricePennies",
                "primaryColor",
                "secondaryColor",
                "userLimit",
                "users",
                "itrentUniqueIdentifier",
                "itrentAlertEmails",
                {
                  name: "links",
                  attributes: [
                    "name",
                    "link",
                    "createdAt",
                    "updatedAt",
                    "linkType",
                    "fileboyId",
                    "folder",
                  ],
                  filter: {
                    matches: {
                      archived: false,
                    },
                  },
                },
                {
                  name: "plan",
                  attributes: [
                    "name",
                    "pricePenniesPerUser",
                    "minUsers",
                    "maxUsers",
                    "includesOneHourHR",
                  ],
                },
              ],
            },
          ])
          meCache.set("_", mePromise)
        }

        const me = await mePromise
        setUser(me)
      } catch (e) {
        let eAsString = ""

        try {
          eAsString = String(e)
        } catch (ee) {
          // can't shove that into the string initialiser
        }

        const isFetchMeNotFound = errorIs(e, "skeem.client.fetchMeNotFound")
        const isRecordNotFound = errorIs(e, "skeem.recordNotFound")

        if (
          isFetchMeNotFound ||
          isRecordNotFound ||
          eAsString.includes("Can't use fetchMe when not logged in")
        ) {
          await fetchMeAndSetRoles()
        } else {
          throw e
        }
      }
    }
    fn()
  }, [...deps])

  return user
}
